import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { playersReportEnabled, resultReportingSettings } from "../lib/resultReporting";
import {
  api,
  ApiError,
  COMMUNICATION_AUDIENCES,
  COMMUNICATION_CHANNELS,
  COMMUNICATION_TYPES,
  participantDisplayName,
  type CommunicationAudience,
  type CommunicationChannel,
  type CommunicationType,
  type Tournament,
  type TournamentCommunication,
  type TournamentGroup,
  type TournamentParticipant,
  type AutoNoticesPayload,
  type MessageTestResult,
  type TournamentMessage,
  type TournamentMessagesPayload,
  type TournamentSettings,
  type TournamentWhatsappGroup,
} from "../lib/api";
import FieldHint from "../components/FieldHint";
import RefereesOptionCard from "../components/RefereesOptionCard";
import WhatsappGroupsEditor from "../components/WhatsappGroupsEditor";
import MessageTestResultCard, {
  MessageTestButton,
  messageTestHasFailure,
  summarizeMessageTest,
} from "../components/MessageTestResult";
import TournamentBottomNav from "../components/TournamentBottomNav";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import { formatDateTime } from "../lib/dateFormat";
import GuideHelpButton from "../components/GuideHelpButton";
import { repeatedTournamentGroupNames, tournamentGroupLabel } from "../lib/groupLabels";

const TYPE_LABELS: Record<string, string> = {
  general: "General",
  schedule_change: "Cambio de horario",
  group_change: "Cambio de grupo",
  result: "Resultado",
  standings: "Standings",
  rules: "Reglas",
  player_change: "Cambio de jugador",
};

const AUDIENCE_LABELS: Record<string, string> = {
  all_players: "Todos los jugadores",
  group: "Grupo",
  player: "Jugador",
  admins: "Administradores",
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  published: "Publicado",
};

const CHANNEL_LABELS: Record<string, string> = {
  web: "Web",
  email: "Email",
  whatsapp: "WhatsApp",
};

const OUTBOX_STATUS_LABELS: Record<string, [string, string]> = {
  sent: ["enviado", "enviados"],
  delivered: ["entregado", "entregados"],
  pending: ["pendiente", "pendientes"],
  failed: ["fallido", "fallidos"],
  skipped: ["omitido", "omitidos"],
};

/** «3 envíos · 2 enviados · 1 pendiente» en lugar de «3 · pending:1 sent:2». */
function describeOutbox(summary: TournamentCommunication["outbox_summary"]): string | null {
  if (!summary || summary.total <= 0) return null;
  const parts = [`${summary.total} ${summary.total === 1 ? "envío" : "envíos"}`];
  for (const [status, count] of Object.entries(summary.by_status)) {
    if (!count) continue;
    const labels = OUTBOX_STATUS_LABELS[status];
    parts.push(`${count} ${labels ? labels[count === 1 ? 0 : 1] : status}`);
  }
  return parts.join(" · ");
}

function normalizeSearch(value: string | null | undefined): string {
  return (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

const COMMS_PAGE_SIZE = 25;

function describeAudience(
  communication: TournamentCommunication,
  groupLabelById?: ReadonlyMap<number, string>,
): string {
  if (communication.audience_type === "group") {
    // «Grupo 1» puede repetirse entre categorías: si el grupo está en la
    // lista cargada, va con su categoría cuando hace falta distinguirlo.
    const label =
      (communication.group_id !== null && communication.group_id !== undefined
        ? groupLabelById?.get(communication.group_id)
        : undefined)
      ?? communication.group_name
      ?? `#${communication.group_id ?? "?"}`;
    return `Grupo · ${label}`;
  }
  if (communication.audience_type === "player") {
    return `Jugador · ${
      communication.player_display_name ?? `#${communication.player_profile_id ?? "?"}`
    }`;
  }
  return AUDIENCE_LABELS[communication.audience_type] ?? communication.audience_type;
}

interface DraftFormState {
  title: string;
  body: string;
  type: CommunicationType | string;
  audience: CommunicationAudience | string;
  groupId: number | "";
  playerProfileId: number | "";
  channels: Record<CommunicationChannel, boolean>;
}

const DEFAULT_FORM: DraftFormState = {
  title: "",
  body: "",
  type: "general",
  audience: "all_players",
  groupId: "",
  playerProfileId: "",
  channels: { web: true, email: false, whatsapp: false },
};

const OPERATIONAL_TEMPLATES: Array<{
  key: string;
  label: string;
  title: string;
  body: string;
  type: CommunicationType | string;
  audience: CommunicationAudience | string;
  channels: Record<CommunicationChannel, boolean>;
}> = [
  {
    key: "welcome-approved",
    label: "Bienvenida / inscripción autorizada",
    title: "Bienvenido al torneo",
    body: "Hola {{jugador}},\n\nTu inscripción fue autorizada. Ya formas parte de {{torneo}}.\n\nPronto recibirás por este medio cualquier indicación operativa: categoría, calendario, reglamento y avisos importantes.\n\nGracias por inscribirte.",
    type: "player_change",
    audience: "player",
    channels: { web: true, email: true, whatsapp: false },
  },
  {
    key: "documents-pending",
    label: "Recordatorio: documentos pendientes",
    title: "Recordatorio: documentos pendientes para tu inscripción",
    body: "Hola {{jugador}},\n\nTe recordamos que para completar tu inscripción a {{torneo}} faltan tus documentos pendientes.\n\nSúbelos desde tu inscripción o contacta al organizador si tienes dudas.",
    type: "player_change",
    audience: "player",
    channels: { web: true, email: true, whatsapp: true },
  },
  {
    key: "payment-pending",
    label: "Recordatorio: pago pendiente",
    title: "Recordatorio: pago pendiente de inscripción",
    body: "Hola {{jugador}},\n\nTu registro a {{torneo}} está recibido, pero el pago sigue pendiente de confirmación.\n\nPor favor paga o envía tu comprobante, o contacta al organizador para aclararlo.",
    type: "player_change",
    audience: "player",
    channels: { web: true, email: true, whatsapp: true },
  },
  {
    key: "group-playoff-classification",
    label: "Clasificación a playoffs",
    title: "Clasificación a playoffs publicada",
    body: "Hola,\n\nYa está publicada la clasificación de la fase de grupos de {{torneo}}.\n\nRevisa tu grupo, posición y criterio de pase en Ranking/Clasificación. Los clasificados avanzan al bracket de playoffs; los cruces reales aparecerán en Calendario y Resultados cuando el organizador los genere.\n\nSi hay empate, se aplica el criterio configurado por el torneo.",
    type: "standings",
    audience: "all_players",
    channels: { web: true, email: true, whatsapp: true },
  },
  {
    key: "playoff-schedule-ready",
    label: "Cruces de playoffs listos",
    title: "Cruces de playoffs listos",
    body: "Hola,\n\nLos cruces de playoffs de {{torneo}} ya están generados.\n\nConsulta Calendario para fecha, hora y cancha. Después de jugar, el resultado se captura en Resultados y el ganador avanza a la siguiente ronda.\n\nPor favor confirma tu disponibilidad con el organizador si aplica.",
    type: "schedule_change",
    audience: "all_players",
    channels: { web: true, email: true, whatsapp: true },
  },
  {
    key: "weekly-schedule",
    label: "Calendario de la semana",
    title: "Calendario de la semana publicado",
    body: "Hola,\n\nYa está publicado el calendario de juegos de {{torneo}}.\n\nConsulta tu día, hora y cancha en la sección Calendario. Si no puedes jugar en tu horario asignado, avisa al organizador lo antes posible para reagendar.\n\n¡Buen partido!",
    type: "schedule_change",
    audience: "all_players",
    channels: { web: true, email: true, whatsapp: true },
  },
  {
    key: "round-results-ranking",
    label: "Resultados y ranking",
    title: "Resultados de la jornada y ranking actualizado",
    body: "Hola,\n\nYa están capturados los resultados de la jornada en {{torneo}} y el ranking quedó actualizado.\n\nRevisa tu posición en la sección Ranking. Si detectas un error en algún marcador, repórtalo al organizador para corregirlo.",
    type: "standings",
    audience: "all_players",
    channels: { web: true, email: true, whatsapp: true },
  },
  {
    key: "rules-reminder",
    label: "Recordatorio de reglamento",
    title: "Recordatorio: reglamento del torneo",
    body: "Hola,\n\nTe recordamos que el reglamento de {{torneo}} está disponible en la sección Reglas.\n\nPuntos clave: puntualidad en los partidos, captura correcta del marcador y política de walkover. Participar en el torneo implica aceptar el reglamento.",
    type: "rules",
    audience: "all_players",
    channels: { web: true, email: true, whatsapp: false },
  },
  {
    key: "closing-awards",
    label: "Cierre y premiación",
    title: "¡Gracias por competir!",
    body: "Hola,\n\n{{torneo}} llegó a su final. ¡Gracias a todos por competir!\n\nConsulta los resultados y posiciones finales en la plataforma. La premiación se realizará según lo indicado por el organizador.\n\n¡Nos vemos en el próximo torneo! 🎾",
    type: "general",
    audience: "all_players",
    channels: { web: true, email: true, whatsapp: true },
  },
];

interface Props {
  setupMode?: boolean;
}

export default function AdminTournamentCommunicationsPage({ setupMode = false }: Props) {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { askConfirm } = useAbortableConfirm([tournamentId]);
  const { token, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Eduardo 2026-09-22: desde el wizard se llega aquí a VER un aviso
  // (?aviso=kind&from=wizard) y se regresa con un botón, sin perder el paso.
  const { highlightedNotice, fromWizard } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return { highlightedNotice: params.get("aviso"), fromWizard: params.get("from") === "wizard" };
  }, [location.search]);
  const wizardCommunicationPath = `/admin/tournaments/${tournamentId}/setup/settings`;

  const [tournament, setTournament] = useState<Tournament | null>(null);
  // Todos los grupos de WhatsApp del torneo (Eduardo 2026-10-01).
  const [whatsappGroups, setWhatsappGroups] = useState<TournamentWhatsappGroup[]>([]);
  const [communications, setCommunications] = useState<TournamentCommunication[]>([]);
  const [commSearch, setCommSearch] = useState("");
  const [commSearchDebounced, setCommSearchDebounced] = useState("");
  const [commStatus, setCommStatus] = useState<string>("all");
  const [commType, setCommType] = useState<string>("all");
  const [commChannel, setCommChannel] = useState<string>("all");
  const [commOrigin, setCommOrigin] = useState<"all" | "auto" | "manual">("all");
  const [commLimit, setCommLimit] = useState(COMMS_PAGE_SIZE);
  const [highlightCommId, setHighlightCommId] = useState<number | null>(null);
  const [groups, setGroups] = useState<TournamentGroup[]>([]);
  const groupLabelById = useMemo(() => {
    const repeated = repeatedTournamentGroupNames(groups);
    return new Map(groups.map((g) => [g.id, tournamentGroupLabel(g, repeated)] as const));
  }, [groups]);
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  // Paso 9 del wizard (feedback Eduardo 2026-07-15): la config de avisos vive
  // aquí mismo y el cierre publica el torneo — el wizard termina de verdad.
  const [settings, setSettings] = useState<TournamentSettings | null>(null);
  // ¿La plataforma tiene WhatsApp conectado? (Meta Cloud API). null = aún no se sabe.
  const [whatsappConnected, setWhatsappConnected] = useState<boolean | null>(null);
  // Avisos automáticos (catálogo de lo que sale solo + los ya enviados por WhatsApp/Email).
  const [autoNotices, setAutoNotices] = useState<AutoNoticesPayload | null>(null);
  // Catálogo de mensajes del torneo con el correo real (Eduardo 2026-09-23).
  const [tournamentMessages, setTournamentMessages] = useState<TournamentMessagesPayload | null>(null);
  const [openPreview, setOpenPreview] = useState<string | null>(null);
  // Mensajes del torneo (maqueta 2026-10-11): pestañas Correo / App / WhatsApp.
  const [previewTab, setPreviewTab] = useState<"email" | "app" | "whatsapp">("email");
  const [suggestingKey, setSuggestingKey] = useState<string | null>(null);
  const [suggestionText, setSuggestionText] = useState("");
  const [messageBusy, setMessageBusy] = useState<string | null>(null);
  // «Enviármelo de prueba»: qué pasó en cada canal (correo, app, WhatsApp).
  const [testResult, setTestResult] = useState<MessageTestResult | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [form, setForm] = useState<DraftFormState>(DEFAULT_FORM);

  const loadContext = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    setContextLoading(true);
    setContextError(null);
    try {
      const [tResp, gResp, pResp] = await Promise.all([
        api.tournaments.get(token, tournamentId),
        api.groups.list(token, tournamentId),
        api.participants.list(token, tournamentId),
      ]);
      setTournament(tResp.data.tournament);
      const firstGroupLink = (tResp.data.tournament.whatsapp_group_link ?? "").trim();
      setWhatsappGroups(
        tResp.data.whatsapp_groups ?? (firstGroupLink ? [{ label: null, url: firstGroupLink }] : []),
      );
      setSettings(tResp.data.settings);
      setGroups(gResp.data.groups);
      setParticipants(pResp.data.participants);
      // Best-effort: si falla, el wizard solo deja de decir "conectado".
      api.communications
        .providersStatus(token, tournamentId)
        .then((response) => setWhatsappConnected(Boolean(response.data.providers.whatsapp?.configured)))
        .catch(() => setWhatsappConnected(null));
      api.communications
        .autoNotices(token, tournamentId)
        .then((response) => setAutoNotices(response.data))
        .catch(() => setAutoNotices(null));
      api.communications
        .messages(token, tournamentId)
        .then((response) => setTournamentMessages(response.data))
        .catch(() => setTournamentMessages(null));
    } catch (err) {
      const nextError = err instanceof ApiError
        ? err.message || "No pudimos cargar el contexto del torneo."
        : "Error de red. Intenta otra vez.";
      setContextError(nextError);
      // In setup mode the terminal context state owns this error. Keep the
      // operational communications/action channel separate so a successful
      // retry cannot leave (or erase) an unrelated alert.
      if (!setupMode) setError(nextError);
    } finally {
      setContextLoading(false);
    }
  }, [token, tournamentId, setupMode]);

  const loadCommunications = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.communications.list(token, tournamentId);
      setCommunications(response.data.communications);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los comunicados.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void loadContext();
  }, [loadContext]);

  useEffect(() => {
    void loadCommunications();
  }, [loadCommunications]);

  useEffect(() => {
    if (!highlightedNotice || !tournamentMessages) return;
    setOpenPreview(highlightedNotice);
    const node = document.getElementById(`aviso-${highlightedNotice}`);
    if (node) window.requestAnimationFrame(() => node.scrollIntoView({ behavior: "smooth", block: "center" }));
  }, [highlightedNotice, tournamentMessages]);

  const activeParticipants = useMemo(
    () =>
      participants.filter(
        (p) => p.current_status !== "dropped" && p.player !== null,
      ),
    [participants],
  );

  function selectedChannels(state: DraftFormState): CommunicationChannel[] {
    return COMMUNICATION_CHANNELS.filter((ch) => state.channels[ch]);
  }

  function applyTemplate(template: (typeof OPERATIONAL_TEMPLATES)[number]) {
    // {{jugador}} stays as a placeholder until the admin explicitly picks the
    // recipient — auto-selecting the first player risked sending a personal
    // notice to the wrong person.
    setForm({
      title: template.title.replaceAll("{{torneo}}", tournament?.name ?? "el torneo"),
      body: template.body.replaceAll("{{torneo}}", tournament?.name ?? "el torneo"),
      type: template.type,
      audience: template.audience,
      groupId: "",
      playerProfileId: "",
      channels: template.channels,
    });
    setMessage(
      template.audience === "player"
        ? `Plantilla cargada: ${template.label}. Elige al jugador destinatario para completar el mensaje.`
        : `Plantilla cargada: ${template.label}. Revisa el mensaje antes de publicar.`,
    );
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    setMessage(null);
    setError(null);
    if (form.title.trim() === "" || form.body.trim() === "") {
      setError("Título y mensaje son obligatorios.");
      return;
    }
    if (form.audience === "group" && form.groupId === "") {
      setError("Selecciona un grupo para esta audiencia.");
      return;
    }
    if (form.audience === "player" && form.playerProfileId === "") {
      setError("Selecciona un jugador para esta audiencia.");
      return;
    }
    if (form.title.includes("{{") || form.body.includes("{{")) {
      setError(
        "El mensaje todavía tiene variables de plantilla sin completar (p. ej. {{jugador}}). Selecciona al jugador o edita el texto.",
      );
      return;
    }
    const channels = selectedChannels(form);
    if (channels.length === 0) {
      setError("Selecciona al menos un canal.");
      return;
    }

    setSaving(true);
    try {
      const created = await api.communications.create(token, tournamentId, {
        title: form.title.trim(),
        body: form.body.trim(),
        communication_type: form.type,
        audience_type: form.audience,
        group_id: form.audience === "group" ? Number(form.groupId) : null,
        player_profile_id:
          form.audience === "player" ? Number(form.playerProfileId) : null,
        channels,
      });
      setMessage("Borrador guardado: publícalo cuando esté listo.");
      setForm(DEFAULT_FORM);
      await loadCommunications();
      const newId = created?.data?.communication?.id;
      if (typeof newId === "number") {
        // El borrador nuevo sale arriba (los borradores van primero); quitamos
        // filtros que pudieran esconderlo y lo llevamos a la vista.
        setCommStatus("all");
        setCommType("all");
        setCommChannel("all");
        setCommOrigin("all");
        setCommSearch("");
        setCommSearchDebounced("");
        setHighlightCommId(newId);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo guardar el comunicado.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish(communicationId: number) {
    if (!token) return;
    setBusyId(communicationId);
    setMessage(null);
    setError(null);
    try {
      await api.communications.publish(token, tournamentId, communicationId);
      setMessage("Comunicado publicado.");
      await loadCommunications();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo publicar.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBusyId(null);
    }
  }

  async function handleDeliver(
    communicationId: number,
    channel: "email" | "whatsapp",
  ) {
    if (!token) return;
    setBusyId(communicationId);
    setMessage(null);
    setError(null);
    try {
      const response =
        channel === "email"
          ? await api.communications.deliverEmail(token, tournamentId, communicationId)
          : await api.communications.deliverWhatsapp(token, tournamentId, communicationId);
      const summary = response.data;
      if (!summary.configured) {
        setMessage(
          `El proveedor de ${CHANNEL_LABELS[channel]} no está configurado en el servidor; ${summary.pending} mensaje(s) quedaron pendientes.`,
        );
      } else {
        setMessage(
          `${CHANNEL_LABELS[channel]}: ${summary.sent} enviado(s), ${summary.failed} fallido(s).`,
        );
      }
      await loadCommunications();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo reenviar.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(communicationId: number) {
    if (!token) return;
    if (!(await askConfirm("¿Eliminar este borrador?", { acceptLabel: "Sí, eliminar borrador", tone: "danger" }))) return;
    setBusyId(communicationId);
    setMessage(null);
    setError(null);
    try {
      await api.communications.remove(token, tournamentId, communicationId);
      setMessage("Borrador eliminado.");
      await loadCommunications();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo eliminar.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBusyId(null);
    }
  }

  async function copyWhatsAppMessage(communication: TournamentCommunication) {
    const text = `${communication.title}\n\n${communication.body}`.trim();
    try {
      await navigator.clipboard.writeText(text);
      setMessage("Mensaje copiado. Abre el grupo de WhatsApp y pégalo ahí.");
      setError(null);
    } catch {
      setError("No se pudo copiar automáticamente. Selecciona el texto del comunicado y cópialo manualmente.");
      setMessage(null);
    }
  }

  async function handleFinishSetup() {
    // Paso 4 (Eduardo 2026-09-23): aquí solo se guarda la configuración y se
    // sigue a Inscripciones. Publicar el torneo vive al final de Calendario
    // (paso 10, Eduardo 2026-10-05).
    if (!token || !settings || !tournament) return;
    setFinishing(true);
    setError(null);
    setMessage(null);
    try {
      await api.tournaments.updateSettings(token, settings.tournament_id, {
        allow_player_result_reporting: settings.allow_player_result_reporting,
        result_reporting_mode: settings.result_reporting_mode,
        auto_generate_announcements: settings.auto_generate_announcements,
        email_notifications_enabled: settings.email_notifications_enabled,
        whatsapp_notifications_enabled: settings.whatsapp_notifications_enabled,
        auto_send_welcome: settings.auto_send_welcome ?? true,
        referees_enabled: Boolean(settings.referees_enabled),
      });
      navigate(`/admin/tournaments/${tournament.id}/setup/registrations`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red. Intenta otra vez.");
    } finally {
      setFinishing(false);
    }
  }

  // Rediseno paso 9 (mockup aprobado por Eduardo 2026-07-30): cada control
  // con titulo + una frase de que pasa al prenderlo, agrupados en tarjetas (el calendario de canchas
  // ya no va aquí: se decide en la sección Calendario, Eduardo 2026-10-03).
  const emailOn = Boolean(settings?.email_notifications_enabled);
  const whatsappOn = Boolean(settings?.whatsapp_notifications_enabled);
  const autoOn = Boolean(settings?.auto_generate_announcements);
  const catalogMessages = tournamentMessages?.messages ?? [];

  const MODE_LABEL: Record<string, string> = {
    auto: "Automático",
    button: "Con botón en Inscripciones",
  };

  async function handleTestSend(message: TournamentMessage) {
    // «Entrar como admin»: la prueba le llegaría al organizador, no a ti (el
    // servidor también la rechaza).
    if (!token || isImpersonating) return;
    setMessageBusy(message.key);
    setError(null);
    setMessage(null);
    setTestResult(null);
    try {
      const response = await api.communications.messageTestSend(token, tournamentId, message.key);
      setTestResult(response.data);
      const summary = `«${message.label}» de prueba: ${summarizeMessageTest(response.data)}.`;
      // Un canal que falló al enviar no va en verde (el detalle está en la tarjeta).
      if (messageTestHasFailure(response.data)) setError(summary);
      else setMessage(summary);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setMessageBusy(null);
    }
  }

  async function handleSuggest(message: TournamentMessage) {
    if (!token) return;
    setMessageBusy(message.key);
    setError(null);
    setMessage(null);
    try {
      await api.communications.messageSuggest(token, tournamentId, message.key, suggestionText);
      setMessage(`Gracias: tu propuesta para «${message.label}» llegó a soporte de Torneos123.`);
      setSuggestingKey(null);
      setSuggestionText("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setMessageBusy(null);
    }
  }

  useEffect(() => {
    const handle = window.setTimeout(() => setCommSearchDebounced(commSearch), 250);
    return () => window.clearTimeout(handle);
  }, [commSearch]);

  useEffect(() => {
    setCommLimit(COMMS_PAGE_SIZE);
  }, [commSearchDebounced, commStatus, commType, commChannel, commOrigin]);

  const commsNewestFirst = useMemo(() => {
    const stamp = (c: TournamentCommunication) =>
      Date.parse(c.published_at ?? c.created_at ?? "") || 0;
    // Borradores siempre primero: tras «Guardar borrador» no deben perderse en la lista.
    const draftRank = (c: TournamentCommunication) => (c.status === "draft" ? 0 : 1);
    return [...communications].sort(
      (a, b) => draftRank(a) - draftRank(b) || stamp(b) - stamp(a) || b.id - a.id,
    );
  }, [communications]);

  useEffect(() => {
    if (highlightCommId === null) return;
    const row = document.getElementById(`comunicado-${highlightCommId}`);
    if (!row) return;
    row.scrollIntoView?.({ behavior: "smooth", block: "center" });
    row.focus?.({ preventScroll: true });
    const handle = window.setTimeout(() => setHighlightCommId(null), 4000);
    return () => window.clearTimeout(handle);
  }, [highlightCommId, communications]);

  const commHasOrigins = useMemo(
    () =>
      communications.some((c) => c.created_by_user_id === null) &&
      communications.some((c) => c.created_by_user_id !== null),
    [communications],
  );

  const commTypesPresent = useMemo(
    () => Array.from(new Set(communications.map((c) => c.communication_type))),
    [communications],
  );

  const commStatusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of communications) counts.set(c.status, (counts.get(c.status) ?? 0) + 1);
    return counts;
  }, [communications]);

  const commsFiltered = useMemo(() => {
    const q = normalizeSearch(commSearchDebounced.trim());
    return commsNewestFirst.filter((c) => {
      if (commStatus !== "all" && c.status !== commStatus) return false;
      if (commType !== "all" && c.communication_type !== commType) return false;
      if (commChannel !== "all" && !c.channels.includes(commChannel)) return false;
      if (commOrigin === "auto" && c.created_by_user_id !== null) return false;
      if (commOrigin === "manual" && c.created_by_user_id === null) return false;
      if (q) {
        const haystack = normalizeSearch(
          [c.title, c.body, describeAudience(c, groupLabelById), c.player_display_name, c.group_name].join(" "),
        );
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [commsNewestFirst, commSearchDebounced, commStatus, commType, commChannel, commOrigin, groupLabelById]);

  const commFiltersActive =
    commSearch.trim() !== "" ||
    commStatus !== "all" ||
    commType !== "all" ||
    commChannel !== "all" ||
    commOrigin !== "all";

  function clearCommFilters() {
    setCommSearch("");
    setCommSearchDebounced("");
    setCommStatus("all");
    setCommType("all");
    setCommChannel("all");
    setCommOrigin("all");
  }

  /** Chips de canal de un mensaje: navy = sale por ahí, gris = apagado. */
  function channelChips(entry: TournamentMessage) {
    const emailActive = emailOn || entry.mode === "button" || entry.key === "bienvenida";
    return (
      <span className="wiz-chips">
        <span className="wiz-chip is-on">App</span>
        {entry.channels.includes("email") ? (
          <span className={`wiz-chip${emailActive ? " is-on" : ""}`}>Correo{emailActive ? "" : " (apagado)"}</span>
        ) : null}
        {entry.channels.includes("whatsapp") ? (
          <span className={`wiz-chip${whatsappOn ? " is-on" : ""}`}>WhatsApp{whatsappOn ? "" : " (apagado)"}</span>
        ) : null}
      </span>
    );
  }

  /** Wizard: tabla «Avisos automáticos»; cada fila abre el mensaje en
   * «Mensajes del torneo» tal como le llega al jugador. */
  function autoNoticeChecklist() {
    if (!settings) return null;
    const canales = ["la app", emailOn ? "correo" : null, whatsappOn ? "WhatsApp" : null].filter(Boolean).join(", ").replace(/, ([^,]*)$/, " y $1");
    return (
      <section className="card wiz-avisos" data-no-toast>
        <header className="wiz-card__head"><h2>Avisos automáticos</h2><p>Cuándo sale cada uno.</p></header>
        <p className="muted">
          {autoOn
            ? `Así le avisa Torneos123 a cada jugador (por ${canales}). Salen solos en el momento indicado; los que dicen «lo mandas tú» no salen solos: los envías desde Inscripciones cuando haga falta.`
            : "Enciende «Anuncios automáticos» (abajo) para que estos mensajes le lleguen solos al jugador:"}
        </p>
        <div className="wiz-avisos__table" role="table" aria-label="Avisos automáticos">
          <div className="wiz-avisos__row wiz-avisos__row--head" role="row">
            <span role="columnheader">Aviso</span>
            <span role="columnheader">Cuándo</span>
            <span role="columnheader">Canales</span>
            <span role="columnheader"></span>
          </div>
          {catalogMessages.filter((entry) => entry.available).map((entry) => (
            <div className="wiz-avisos__row" role="row" key={entry.key}>
              <strong role="cell">
                {entry.label}
                {entry.mode === "button" ? <span className="kbd"> lo mandas tú</span> : null}
              </strong>
              <span role="cell" className="muted">{entry.when}</span>
              <span role="cell">{channelChips(entry)}</span>
              <span role="cell">
                <Link
                  className="wiz-link"
                  to={`/admin/tournaments/${tournamentId}/communications?aviso=${entry.key}&from=wizard`}
                  title="Ver el correo tal como le llega al jugador"
                >
                  Ver mensaje ›
                </Link>
              </span>
            </div>
          ))}
          {catalogMessages.length === 0 ? <p className="muted">Cargando la lista de mensajes…</p> : null}
        </div>
        <p className="muted">
          Toca un mensaje para ver el correo tal como le llega al jugador. Lo que quieras decirles tú (calendario de la semana, cierre, etc.) lo escribes en Comunicados.
          {whatsappOn && whatsappConnected === false ? " Hoy la plataforma no tiene WhatsApp conectado: por ahí no saldrá nada hasta que se conecte." : ""}
        </p>
      </section>
    );
  }

  /** Operación: «Mensajes del torneo» — lista a la izquierda, detalle con
   * pestañas Correo / App / WhatsApp a la derecha (maqueta 2026-10-11). */
  function autoNoticeCatalog() {
    if (!tournamentMessages) return null;
    const channels = tournamentMessages.channels;
    const list = tournamentMessages.messages;
    const entry = list.find((m) => m.key === openPreview) ?? list[0];
    const hasEmail = entry ? entry.channels.includes("email") : false;
    const hasWhatsapp = entry ? entry.channels.includes("whatsapp") : false;
    const tab = previewTab === "email" && !hasEmail ? "app" : previewTab === "whatsapp" && !hasWhatsapp ? "app" : previewTab;
    return (
      <section className="card wiz-mensajes" data-no-toast id="avisos-automaticos">
        <div className="card__header-row">
          <div>
            <p className="wiz-eyebrow">Comunicación{tournament ? ` · ${tournament.name}` : ""}</p>
            <h2 className="card__title">Mensajes del torneo</h2>
            <p className="card__subtitle">
              Lo que el sistema le dice a cada jugador, con el texto exacto que le llega. Por la app siempre
              {channels.email ? ", por correo" : ""}{channels.whatsapp ? " y por WhatsApp" : ""}.
              {!channels.auto_generate_announcements ? " Hoy los automáticos están apagados: enciende «Anuncios automáticos» en Comunicación." : ""}
              {!channels.group_link ? " Sin link del grupo de WhatsApp (tarjeta «Grupos de WhatsApp»): la bienvenida y el pago confirmado no lo incluyen." : ""}
            </p>
          </div>
          {/* El «Regresar a Comunicación» vive arriba, en la cabecera de la página. */}
        </div>
        <div className="wiz-mensajes__grid">
          <ol className="wiz-mensajes__list">
            {list.map((m) => (
              <li key={m.key} id={`aviso-${m.key}`}>
                <button
                  type="button"
                  className={`wiz-mensajes__item${entry?.key === m.key ? " is-selected" : ""}${m.available ? "" : " is-soon"}`}
                  aria-pressed={entry?.key === m.key}
                  onClick={() => setOpenPreview(m.key)}
                >
                  <strong>{m.label}</strong>
                  <span className="wiz-mensajes__meta">
                    {m.available ? MODE_LABEL[m.mode] : "Próximamente"} · App
                    {m.channels.includes("email") ? " · Correo" : ""}
                    {m.channels.includes("whatsapp") ? " · WhatsApp" : ""}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          {entry ? (
            <div className="wiz-mensajes__detail">
              <h3>{entry.label}</h3>
              <p className="muted">{entry.when}</p>
              {entry.detail ? <p className="muted">{entry.detail}</p> : null}
              {channelChips(entry)}
              <div className="form__actions">
                <MessageTestButton
                  channels={entry.channels}
                  busy={messageBusy === entry.key}
                  impersonating={isImpersonating}
                  onSend={() => void handleTestSend(entry)}
                />
                <button
                  type="button"
                  className="btn btn--ghost btn--compact"
                  onClick={() => { setSuggestingKey(suggestingKey === entry.key ? null : entry.key); setSuggestionText(""); }}
                >
                  Proponer otro mensaje
                </button>
              </div>
              {testResult?.key === entry.key ? <MessageTestResultCard result={testResult} /> : null}
              {suggestingKey === entry.key ? (
                <div className="form aviso-catalogo__suggest">
                  <label>
                    Tu propuesta de texto para «{entry.label}»
                    <textarea rows={4} value={suggestionText} onChange={(e) => setSuggestionText(e.target.value)} placeholder="Escribe cómo te gustaría que dijera este mensaje." />
                  </label>
                  <div className="form__actions">
                    <button type="button" className="btn btn--compact" onClick={() => void handleSuggest(entry)} disabled={messageBusy === entry.key || suggestionText.trim().length < 10}>
                      Enviar propuesta a soporte
                    </button>
                  </div>
                </div>
              ) : null}
              <div className="wiz-tabs" role="tablist" aria-label="Canal de la vista previa">
                {hasEmail ? (
                  <button type="button" role="tab" aria-selected={tab === "email"} className={tab === "email" ? "is-active" : ""} onClick={() => setPreviewTab("email")}>Correo</button>
                ) : null}
                <button type="button" role="tab" aria-selected={tab === "app"} className={tab === "app" ? "is-active" : ""} onClick={() => setPreviewTab("app")}>App</button>
                {hasWhatsapp ? (
                  <button type="button" role="tab" aria-selected={tab === "whatsapp"} className={tab === "whatsapp" ? "is-active" : ""} onClick={() => setPreviewTab("whatsapp")}>
                    WhatsApp{channels.whatsapp ? "" : " · apagado"}
                  </button>
                ) : null}
              </div>
              {tab === "email" ? (
                <div className="aviso-catalogo__preview">
                  <p className="muted">Asunto: <strong>{entry.subject}</strong></p>
                  <iframe className="aviso-preview-frame" title={`Correo ${entry.label}`} srcDoc={entry.html} sandbox="" />
                </div>
              ) : tab === "whatsapp" ? (
                <div className="aviso-catalogo__preview">
                  {!channels.whatsapp ? <p className="muted">WhatsApp está apagado en este torneo: así se vería si lo enciendes.</p> : null}
                  <p className="wiz-whatsapp-bubble">{entry.whatsapp_text}</p>
                </div>
              ) : (
                <div className="aviso-preview" aria-label={`Ejemplo del mensaje ${entry.label}`}>
                  <span className="aviso-preview__remitente">{tournament?.name ?? "Tu torneo"}</span>
                  <strong>{entry.title}</strong>
                  <p>{entry.body.split("\n\n").slice(1, 3).join(" ")}</p>
                  <span className="aviso-preview__liga">{entry.button_label}</span>
                </div>
              )}
            </div>
          ) : null}
        </div>
        <p className="muted">
          Los nombres y montos son de ejemplo (Ana López, $850): en el mensaje real van los del jugador. El nombre del torneo, el link del grupo y el costo son los tuyos.
        </p>
      </section>
    );
  }

  function channelCard(
    key: "email_notifications_enabled" | "whatsapp_notifications_enabled",
    titulo: string,
    detalle: string,
    ayuda: string,
  ) {
    if (!settings) return null;
    const on = Boolean(settings[key]);
    return (
      <label className={`wiz-canal${on ? " is-on" : ""}`}>
        <strong className="hint-label">
          {titulo}
          <FieldHint>{ayuda}</FieldHint>
        </strong>
        <span className="muted">{detalle}</span>
        <span className="wiz-canal__switch">
          <input
            type="checkbox"
            checked={on}
            onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
            disabled={finishing}
          />
          <span>{on ? "Encendido" : "Apagado"}</span>
        </span>
      </label>
    );
  }

  function avisoRow(
    key:
      | "auto_generate_announcements"
      | "email_notifications_enabled"
      | "whatsapp_notifications_enabled"
      | "auto_send_welcome"
      | "allow_post_publish_changes",
    titulo: string,
    ayuda: string,
    badge?: string,
  ) {
    if (!settings) return null;
    return (
      <label className="aviso-fila">
        <span className="aviso-fila__texto">
          <strong className="hint-label">
            {titulo}
            {badge ? <span className={`kbd aviso-fila__badge${badge === "conectado" ? " kbd--ok" : ""}`}>{badge}</span> : null}
            <FieldHint>{ayuda}</FieldHint>
          </strong>
        </span>
        <input
          type="checkbox"
          checked={Boolean(settings[key])}
          onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
          disabled={finishing}
        />
      </label>
    );
  }

  // UNA sola decision de captura de resultados: antes eran un toggle y un
  // select separados que podian contradecirse (helper compartido con tests).
  const playersReport = settings ? playersReportEnabled(settings) : false;

  function setReporting(playersDo: boolean) {
    if (!settings) return;
    setSettings({ ...settings, ...resultReportingSettings(playersDo) });
  }

  function reportingOption(playersDo: boolean, titulo: string, ayuda: string) {
    const active = playersReport === playersDo;
    return (
      <label className={`aviso-radio${active ? " is-activa" : ""}`}>
        <input
          type="radio"
          name="modo-resultados"
          checked={active}
          onChange={() => setReporting(playersDo)}
          disabled={finishing}
        />
        <strong className="hint-label">
          {titulo}
          <FieldHint>{ayuda}</FieldHint>
        </strong>
      </label>
    );
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  if (setupMode && contextLoading) {
    return (
      <>
        <TournamentSetupProgress current={4} />
        <section className="card" aria-busy="true">
          <p className="muted">Cargando comunicación y configuración…</p>
        </section>
      </>
    );
  }

  if (setupMode && (!tournament || !settings)) {
    return (
      <>
        <TournamentSetupProgress current={4} />
        <section className="card">
          <h1 className="card__title">No pudimos cargar Comunicación</h1>
          <p role="alert" className="form-error">
            {contextError ?? "No pudimos cargar el torneo o su configuración."}
          </p>
          <div className="form__actions">
            <button className="btn" type="button" onClick={() => void loadContext()}>
              Reintentar
            </button>
            <Link className="btn btn--ghost" to={`/admin/tournaments/${tournamentId}/setup/call`}>
              Volver a convocatoria
            </Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <div className={setupMode ? "wiz-comms" : undefined}>
      {setupMode && <TournamentSetupProgress current={4} />}
      {setupMode && (
        <section className="wiz-hero">
          <div>
            <p className="wiz-hero__eyebrow">Paso 4 de 10{tournament ? ` · ${tournament.name}` : ""}</p>
            <h1>
              <span className="hint-label">
                Comunicación
                <GuideHelpButton />
              </span>
            </h1>
            <p>Qué avisos salen solos y por dónde le llegan a cada jugador. El torneo se publica al final de Calendario (paso 10).</p>
          </div>
          <Link className="wiz-hero__cta" to={`/admin/tournaments/${tournamentId}/communications?from=wizard`}>
            Ver y probar mensajes ›
          </Link>
        </section>
      )}
      {setupMode && (error || message) ? (
        <section className="card">
          {error && <p role="alert" className="form-error">{error}</p>}
          {message && <p className="form-message">{message}</p>}
        </section>
      ) : null}
      {!setupMode && (
      <section className="card">
        <div className="card__header-row">
          <div>
            {setupMode ? (
              <h2 className="card__title">
                <span className="hint-label">
                  Comunicados{tournament ? ` · ${tournament.name}` : ""}
                  <GuideHelpButton />
                </span>
              </h2>
            ) : (
              <h1 className="card__title">
                <span className="hint-label">
                  Comunicados{tournament ? ` · ${tournament.name}` : ""}
                  <GuideHelpButton />
                </span>
              </h1>
            )}
            <p className="card__subtitle">
              Avisa a los jugadores cuando cambien horarios, grupos, reglas o
              resultados. Se guardan como borrador y se publican cuando estén
              listos.
            </p>
          </div>
          {fromWizard && !setupMode ? (
            <Link className="btn" to={wizardCommunicationPath}>← Regresar a Comunicación</Link>
          ) : null}
        </div>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}
      </section>
      )}

      {setupMode && settings && tournament && (
        <>
          {/* El calendario de canchas (Sí/No) se decide solo en la sección
              Calendario (Eduardo 2026-10-03): aquí ya no se repite. */}
          <section className="card">
            <header className="wiz-card__head"><h2>Resultados</h2><p>¿Quién captura los resultados?</p></header>
            <div className="aviso-radios">
              {reportingOption(
                false,
                "Solo el organizador",
                "Tú publicas todos los resultados desde el panel.",
              )}
              {reportingOption(
                true,
                "Los jugadores reportan",
                "Cada jugador propone su resultado y tú lo apruebas con un clic.",
              )}
            </div>
          </section>

          {/* Árbitros sí/no (maqueta Wiz-Arbitros-Opcion, Eduardo 2026-10-17):
              se guarda con el resto del paso. */}
          <RefereesOptionCard
            enabled={Boolean(settings.referees_enabled)}
            disabled={finishing}
            onChange={(on) => setSettings({ ...settings, referees_enabled: on })}
          />

          <section className="card wiz-canales-card">
          <header className="wiz-card__head"><h2>Canales</h2><p>Por dónde le llegan los avisos a cada jugador.</p></header>
          <div className="wiz-canales" role="group" aria-label="Canales de aviso">
            <div className="wiz-canal is-on">
              <strong className="hint-label">
                App
                <FieldHint>Todo comunicado aparece en el inicio del jugador. Sin costo.</FieldHint>
              </strong>
              <span className="muted">Siempre activo</span>
              <span className="wiz-chip is-on">Activo</span>
            </div>
            {channelCard(
              "email_notifications_enabled",
              "Correo",
              "Al correo del perfil",
              "Los avisos automáticos de este torneo le llegan al jugador también por correo (al email de su perfil). Tus comunicados llevan Email cuando lo eliges al redactarlos.",
            )}
            {channelCard(
              "whatsapp_notifications_enabled",
              "WhatsApp",
              whatsappConnected ? "Al celular del jugador · conectado" : "Al celular del jugador · requiere proveedor",
              whatsappConnected
                ? "Torneos123 ya tiene WhatsApp conectado. Al encenderlo, los avisos automáticos de este torneo le llegan al jugador también por WhatsApp (al celular de su perfil)."
                : "Se activa cuando la plataforma tiene conectada su línea de WhatsApp.",
            )}
          </div>
          </section>

          {/* Eduardo 2026-10-12: los grupos de WhatsApp se editan SOLO aquí
              (tarjeta 3). Guarda únicamente sus campos. */}
          {token ? (
            <WhatsappGroupsEditor
              token={token}
              tournament={tournament}
              groups={whatsappGroups}
              onSaved={(link, saved) => {
                setTournament((current) => (current ? { ...current, whatsapp_group_link: link } : current));
                setWhatsappGroups(saved);
              }}
            />
          ) : null}

          {/* Eduardo 2026-09-20/22: que el admin vea QUÉ sale solo por Email y
              WhatsApp y pueda abrir cada mensaje tal como le llega al jugador. */}
          {autoNoticeChecklist()}

          <section className="card">
            <header className="wiz-card__head"><h2>Envío automático</h2></header>
            {avisoRow(
              "auto_generate_announcements",
              "Anuncios automáticos",
              "El sistema avisa solo: inscripción recibida, pago confirmado, sorteo, cambio de horario y resultado. Por la app siempre; por Email y WhatsApp si los tienes encendidos arriba.",
            )}
            {avisoRow(
              "auto_send_welcome",
              "Bienvenida automática al aprobar",
              "Al aprobar una inscripción sale sola la bienvenida: usuario y contraseña si la cuenta es nueva, estado del pago y link del grupo de WhatsApp. Apagada, la mandas tú con «Enviar bienvenida» en Inscripciones.",
            )}
          </section>


        </>
      )}

      {/* Eduardo 2026-09-22: primero lo que sale solo (con su texto), después lo
          que manda el organizador. */}
      {!setupMode && autoNoticeCatalog()}

      {/* Redactar comunicados es tarea de OPERACION, no de configuracion:
          en el wizard esta tabla se oculta (pedido Eduardo jul-17). */}
      {!setupMode && (
      <section className="card">
        <h2 className="card__title">Nuevo comunicado</h2>
        <div className="communications-template-panel">
          <div>
            <strong className="hint-label">
              Plantillas para tus comunicados
              <FieldHint>
                Los avisos de arriba salen solos. Estas plantillas son para lo que
                mandas tú: recordatorios, calendario, clasificación, cierre. Cárgalas,
                ajusta el texto y elige los canales.
              </FieldHint>
            </strong>
          </div>
          <div className="communications-template-actions">
            {OPERATIONAL_TEMPLATES.map((template) => (
              <button
                key={template.key}
                className="button button--sm button--ghost"
                type="button"
                onClick={() => applyTemplate(template)}
              >
                {template.label}
              </button>
            ))}
          </div>
        </div>
        <form className="form" onSubmit={handleCreate}>
          <label>
            Título
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              disabled={saving}
              placeholder="Ej. Cambio de horarios — sábado"
            />
          </label>
          <label>
            Tipo
            <select className="t123-select"
              value={form.type}
              onChange={(e) =>
                setForm({ ...form, type: e.target.value as CommunicationType })
              }
              disabled={saving}
            >
              {COMMUNICATION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t] ?? t}
                </option>
              ))}
            </select>
          </label>
          <label>
            Audiencia
            <select className="t123-select"
              value={form.audience}
              onChange={(e) =>
                setForm({
                  ...form,
                  audience: e.target.value as CommunicationAudience,
                  groupId: "",
                  playerProfileId: "",
                })
              }
              disabled={saving}
            >
              {COMMUNICATION_AUDIENCES.map((a) => (
                <option key={a} value={a}>
                  {AUDIENCE_LABELS[a] ?? a}
                </option>
              ))}
            </select>
          </label>
          {form.audience === "group" && (
            <label>
              Grupo
              <select className="t123-select"
                value={form.groupId === "" ? "" : String(form.groupId)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    groupId: e.target.value === "" ? "" : Number(e.target.value),
                  })
                }
                disabled={saving}
                required
              >
                <option value="">Selecciona un grupo…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {groupLabelById.get(g.id) ?? g.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {form.audience === "player" && (
            <label>
              Jugador
              <select className="t123-select"
                value={
                  form.playerProfileId === ""
                    ? ""
                    : String(form.playerProfileId)
                }
                onChange={(e) => {
                  const nextId = e.target.value === "" ? "" : Number(e.target.value);
                  const participant = activeParticipants.find(
                    (p) => p.player_profile_id === nextId,
                  );
                  const playerName = participant
                    ? participantDisplayName(participant)
                    : "";
                  setForm((current) => ({
                    ...current,
                    playerProfileId: nextId,
                    // Complete the template placeholder with the chosen player.
                    title: playerName
                      ? current.title.replaceAll("{{jugador}}", playerName)
                      : current.title,
                    body: playerName
                      ? current.body.replaceAll("{{jugador}}", playerName)
                      : current.body,
                  }));
                }}
                disabled={saving}
                required
              >
                <option value="">Selecciona un jugador…</option>
                {activeParticipants
                  .filter((p) => p.player_profile_id !== null)
                  .slice()
                  .sort((a, b) => participantDisplayName(a).localeCompare(participantDisplayName(b), "es", { sensitivity: "base" }))
                  .map((p) => (
                    <option key={p.id} value={p.player_profile_id ?? undefined}>
                      {participantDisplayName(p)}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <fieldset className="form__fieldset">
            <legend>
              <span className="hint-label">
                Canales
                <FieldHint>
                  Los comunicados quedan guardados para consulta en la app. Para
                  WhatsApp, usa el MVP manual: publica el comunicado, copia el
                  mensaje y abre el grupo del torneo para pegarlo.
                </FieldHint>
              </span>
            </legend>
            <div className="form__checkbox-row">
              {COMMUNICATION_CHANNELS.map((ch) => (
                <label key={ch} className="form__checkbox">
                  <input
                    type="checkbox"
                    checked={form.channels[ch]}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        channels: { ...form.channels, [ch]: e.target.checked },
                      })
                    }
                    disabled={saving}
                  />
                  {CHANNEL_LABELS[ch] ?? ch}
                </label>
              ))}
            </div>
          </fieldset>
          <label>
            Mensaje
            <textarea
              rows={5}
              required
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              disabled={saving}
              placeholder="Escribe el mensaje que verán los jugadores."
            />
          </label>
          <div className="form__actions">
            <button className="btn" type="submit" disabled={saving}>
              {saving ? "Guardando…" : "Guardar borrador"}
            </button>
          </div>
        </form>
      </section>
      )}

      <section className="card">
        {setupMode ? (
          <header className="wiz-card__head"><h2>Comunicados</h2></header>
        ) : (
          <div className="card__header-row">
            <h2 className="card__title">Comunicados</h2>
          </div>
        )}
        {loading ? (
          <p className="muted">Cargando comunicados…</p>
        ) : communications.length === 0 ? (
          <p className="muted">
            {setupMode
              ? "Aún no hay comunicados. Puedes terminar la configuración sin crearlos; los borradores se redactan después desde Comunicación."
              : "Aún no hay comunicados. Crea uno arriba para guardarlo como borrador."}
          </p>
        ) : (
          <>
          <div className="adm-players__toolbar communications-toolbar">
            <label className="adm-players__search">
              <input
                type="search"
                value={commSearch}
                onChange={(e) => setCommSearch(e.target.value)}
                placeholder="Buscar por título, texto o jugador"
                aria-label="Buscar comunicados"
              />
            </label>
            <select
              className="t123-select adm-players__filter"
              value={commType}
              onChange={(e) => setCommType(e.target.value)}
              aria-label="Filtrar por tipo"
            >
              <option value="all">Todos los tipos</option>
              {commTypesPresent.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t] ?? t}
                </option>
              ))}
            </select>
          </div>
          <div className="adm-players__toolbar communications-toolbar">
            <div className="adm-players__chips" role="group" aria-label="Filtrar por estado">
              <button
                type="button"
                className={`adm-players__filter${commStatus === "all" ? " is-active" : ""}`}
                onClick={() => setCommStatus("all")}
              >
                Todos ({communications.length})
              </button>
              {Array.from(commStatusCounts.entries()).map(([st, count]) => (
                <button
                  key={st}
                  type="button"
                  className={`adm-players__filter${commStatus === st ? " is-active" : ""}`}
                  onClick={() => setCommStatus(st)}
                >
                  {STATUS_LABELS[st] ?? st} ({count})
                </button>
              ))}
            </div>
            <div className="adm-players__chips" role="group" aria-label="Filtrar por canal">
              {(["all", "web", "email", "whatsapp"] as const).map((ch) => (
                <button
                  key={ch}
                  type="button"
                  className={`adm-players__filter${commChannel === ch ? " is-active" : ""}`}
                  onClick={() => setCommChannel(ch)}
                >
                  {ch === "all" ? "Todos los canales" : CHANNEL_LABELS[ch]}
                </button>
              ))}
            </div>
            {commHasOrigins ? (
              <div className="adm-players__chips" role="group" aria-label="Filtrar por origen">
                {(
                  [
                    ["all", "Todos"],
                    ["auto", "Automáticos"],
                    ["manual", "Escritos a mano"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`adm-players__filter${commOrigin === value ? " is-active" : ""}`}
                    onClick={() => setCommOrigin(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="communications-toolbar__meta">
            <span className="muted">
              Mostrando {Math.min(commLimit, commsFiltered.length)} de {communications.length}
              {commsFiltered.length !== communications.length
                ? ` (${commsFiltered.length} con estos filtros)`
                : ""}
            </span>
            {commFiltersActive ? (
              <button type="button" className="btn btn--ghost btn--compact" onClick={clearCommFilters}>
                Limpiar filtros
              </button>
            ) : null}
          </div>
          {commsFiltered.length === 0 ? (
            <p className="muted">Ningún comunicado coincide con los filtros.</p>
          ) : (
          <div className="responsive-table-wrap">
            <table className="data-table communications-table">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Tipo</th>
                  <th>Audiencia</th>
                  <th>Título</th>
                  <th>Publicado</th>
                  <th>Envíos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {commsFiltered.slice(0, commLimit).map((c) => {
                  const outboxText = describeOutbox(c.outbox_summary);
                  return (
                    <tr
                      key={c.id}
                      id={`comunicado-${c.id}`}
                      tabIndex={-1}
                      className={[
                        c.status === "draft" ? "communications-row--draft" : "",
                        highlightCommId === c.id ? "communications-row--just-saved" : "",
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined}
                    >
                      <td>
                        <span
                          className={`status-pill status-pill--${
                            c.status === "published" ? "active" : "dropped"
                          }`}
                        >
                          {STATUS_LABELS[c.status] ?? c.status}
                        </span>
                      </td>
                      <td>{TYPE_LABELS[c.communication_type] ?? c.communication_type}</td>
                      <td>{describeAudience(c, groupLabelById)}</td>
                      <td>
                        <strong>{c.title}</strong>
                        <div className="muted" style={{ fontSize: "0.78rem", marginTop: "0.2rem" }}>
                          {c.channels.map((ch) => CHANNEL_LABELS[ch] ?? ch).join(" · ")}
                        </div>
                      </td>
                      <td>{formatDateTime(c.published_at)}</td>
                      <td>
                        {outboxText ? (
                          <span className="muted communications-outbox">{outboxText}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        {/* Eduardo 2026-10-09: la fila medía ~239 px porque Copiar WhatsApp,
                            Abrir grupo, Reenviar email/WhatsApp y «Visible a jugadores» se
                            apilaban. Publicado: todo va en «Acciones ▾»; borrador: Publicar y
                            Eliminar a la vista. */}
                        <div className="table-actions">
                          {c.status === "draft" ? (
                            <>
                              <button
                                type="button"
                                className="btn btn--compact"
                                onClick={() => void handlePublish(c.id)}
                                disabled={busyId === c.id}
                              >
                                {busyId === c.id ? "Publicando…" : "Publicar"}
                              </button>
                              <button
                                type="button"
                                className="btn btn--ghost btn--compact"
                                onClick={() => void handleDelete(c.id)}
                                disabled={busyId === c.id}
                              >
                                Eliminar
                              </button>
                            </>
                          ) : (c.channels.includes("whatsapp") && whatsappGroups.length > 0) ||
                            (c.status === "published" && (c.channels.includes("email") || c.channels.includes("whatsapp"))) ? (
                            <details className="communications-groups">
                              <summary className="btn btn--ghost btn--compact">
                                {busyId === c.id ? "Enviando…" : "Acciones ▾"}
                              </summary>
                              <div className="communications-groups__list">
                                {c.channels.includes("whatsapp") && whatsappGroups.length > 0 ? (
                                  <>
                                    <button type="button" onClick={() => void copyWhatsAppMessage(c)}>
                                      Copiar WhatsApp
                                    </button>
                                    {whatsappGroups.map((group, index) => (
                                      <a key={`${group.url}-${index}`} href={group.url} target="_blank" rel="noreferrer">
                                        {whatsappGroups.length > 1
                                          ? `Abrir ${group.label?.trim() || `grupo ${index + 1}`}`
                                          : "Abrir grupo de WhatsApp"}
                                      </a>
                                    ))}
                                  </>
                                ) : null}
                                {c.status === "published" && c.channels.includes("email") ? (
                                  <button
                                    type="button"
                                    onClick={() => void handleDeliver(c.id, "email")}
                                    disabled={busyId === c.id}
                                    title="Reintenta los emails pendientes o fallidos de este comunicado"
                                  >
                                    Reenviar email
                                  </button>
                                ) : null}
                                {c.status === "published" && c.channels.includes("whatsapp") ? (
                                  <button
                                    type="button"
                                    onClick={() => void handleDeliver(c.id, "whatsapp")}
                                    disabled={busyId === c.id}
                                    title="Reintenta los WhatsApp pendientes o fallidos de este comunicado"
                                  >
                                    Reenviar WhatsApp
                                  </button>
                                ) : null}
                              </div>
                            </details>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          {commsFiltered.length > commLimit ? (
            <div className="communications-toolbar__more">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setCommLimit((n) => n + COMMS_PAGE_SIZE)}
              >
                Ver más ({commsFiltered.length - commLimit} restantes)
              </button>
            </div>
          ) : null}
          </>
        )}
      </section>
      {/* Eduardo 2026-09-21: qué avisos salieron solos por WhatsApp y si llegaron.
          Aviso de ESTADO (data-no-toast): no debe saltar como toast. */}
      {!setupMode && autoNotices && (autoNotices.channels.email.enabled || autoNotices.channels.whatsapp.enabled || autoNotices.notices.length > 0) ? (
        <section className="card" data-no-toast>
          <h2 className="card__title">Avisos automáticos enviados</h2>
          <p className="card__subtitle">
            Lo que el sistema le avisó solo a cada jugador por WhatsApp y correo, y si le llegó.
            {autoNotices.counts.held > 0
              ? ` ${autoNotices.counts.held} detenido(s) por el candado de pruebas: todavía no se abre el envío a todos los jugadores.`
              : ""}
            {autoNotices.channels.whatsapp.enabled && !autoNotices.channels.whatsapp.configured ? " La plataforma no tiene WhatsApp conectado: por ahí no sale nada." : ""}
            {autoNotices.channels.email.enabled && !autoNotices.channels.email.configured ? " La plataforma no tiene correo configurado: por ahí no sale nada." : ""}
          </p>
          {autoNotices.notices.length === 0 ? (
            <p className="muted">Todavía no sale ningún aviso automático en este torneo.</p>
          ) : (
            <div className="responsive-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Cuándo</th>
                    <th>Aviso</th>
                    <th>Jugador</th>
                    <th>Por</th>
                    <th>A</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {autoNotices.notices.map((notice) => (
                    <tr key={notice.id}>
                      <td>{formatDateTime(notice.sent_at ?? notice.created_at)}</td>
                      <td>{notice.kind_label}</td>
                      <td>{notice.recipient_name ?? "—"}</td>
                      <td>{notice.channel === "email" ? "Email" : "WhatsApp"}</td>
                      <td>{notice.recipient_phone ?? notice.recipient_email ?? "—"}</td>
                      <td>
                        {notice.state === "sent"
                          ? notice.provider_status === "read"
                            ? "Leído"
                            : notice.provider_status === "delivered"
                              ? "Entregado"
                              : "Enviado"
                          : notice.state === "held"
                            ? "Detenido (candado de pruebas)"
                            : notice.state === "failed"
                              ? `No salió${notice.error_message ? `: ${notice.error_message}` : ""}`
                              : "Pendiente"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
      {!setupMode && <TournamentBottomNav tournamentId={tournamentId} />}
      {setupMode && settings && tournament ? (
        <div className="wiz-bottombar">
          <Link className="wiz-bottombar__back" to={`/admin/tournaments/${tournamentId}/setup/call`}>‹ Regresar</Link>
          <button
            className="wiz-bottombar__next"
            type="button"
            onClick={() => void handleFinishSetup()}
            disabled={finishing}
          >
            {finishing ? "Guardando…" : "Guardar y continuar a Inscripciones ›"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
